import React, { ReactNode, useCallback } from "react";
import {
  ActiveElement,
  BubbleDataPoint,
  ChartEvent,
  ChartOptions,
} from "chart.js";
import { map, max } from "lodash";
import { shorten } from "@backpacker/number";
import Tooltip from "@charty/tooltip/chartjs-bubble";
import { Color } from "@glossy";
import { content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { isFloat } from "@tootools/number";
import { BubbleSerie as Serie } from "../models";
import {
  closeCustomTooltip,
  openCustomTooltip,
} from "../tooltip/chartjs-tooltip-root";

type ChartTooltip = {
  label: string;
  color: string;
  size: number;
  xValue: number;
  yValue: number;
};

export const useOnClickPoint = (
  series: Serie[],
  onClick: (serie: Serie["id"]) => void
) =>
  useCallback(
    (datasetIndex: number, index: number) => {
      if (onClick) {
        onClick(series[datasetIndex].id);
      }
    },
    [onClick, series]
  );
export const useOptions = (
  theme: GlossyTheme,
  locale: Locale,
  XMetricLabel: string,
  YMetricLabel: string,
  sizeMetricLabel: string,
  XMetric: Metric,
  onClick: (datasetIndex: number, index: number) => void,
  series: Serie[],
  chartTooltip?: (props: ChartTooltip) => ReactNode,
  scaleX?: "linear" | "logarithmic",
  scaleY?: "linear" | "logarithmic",
  disableAnimation?: boolean,
  isFullscreen: boolean = false
): ChartOptions<"bubble"> => {
  const xMax = max(map(series, "data[0].x")) * 1.05;
  const yMax = max(map(series, "data[0].y")) * 1.15;
  return {
    animation: {
      duration: disableAnimation ? 0 : 1000,
    },
    responsive: true,
    maintainAspectRatio: false,
    onClick: function (evt: ChartEvent, elements: ActiveElement[]) {
      if (onClick) {
        if (!isFullscreen) {
          closeCustomTooltip();
        }
        if (elements && elements[0]) {
          onClick(elements[0].datasetIndex, elements[0].index);
        }
      }
    },
    layout: {
      autoPadding: false,
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      x: {
        type: scaleX,
        suggestedMax: xMax,
        title: {
          display: true,
          text: XMetricLabel,
          color: content({ theme }, "primary"),
          font: { size: 14, weight: "bold" },
        },
        grid: {
          color: content({ theme }, "tertiary"),
        },
        ticks: {
          color: content({ theme }, "secondary"),
          callback: (value: any) =>
            isFloat(value)
              ? undefined
              : XMetric === Metric.NetSentiment
              ? `${shorten(locale, value)}%`
              : shorten(locale, value),
        },
      },
      y: {
        type: scaleY,
        suggestedMax: yMax,
        title: {
          display: true,
          text: YMetricLabel,
          color: content({ theme }, "primary"),
          font: { size: 14, weight: "bold" },
        },
        grid: {
          color: content({ theme }, "tertiary"),
        },
        ticks: {
          color: content({ theme }, "secondary"),
          callback: (value: any) =>
            isFloat(value) ? undefined : shorten(locale, value),
        },
      },
    },
    onHover: function (e: ChartEvent, elements: ActiveElement[]) {
      if (onClick && e.native.target instanceof HTMLElement) {
        const cursor = elements.length ? "var(--cursor-magic-wand)" : "default";
        if (e.native.target.style.cursor !== cursor) {
          e.native.target.style.cursor = cursor;
          e.native.target.dispatchEvent(getOnChangeCanvasCursorEvent());
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "nearest",
        intersect: true,
        position: "average",
        caretPadding: 24,
        enabled: isFullscreen,
        padding: 24,
        backgroundColor: Color.Black,
        titleColor: Color.White,
        titleFont: { size: 14 },
        bodyColor: Color.White,
        bodyFont: { size: 14 },
        bodySpacing: 12,
        callbacks: {
          label: (tooltipModel) => {
            return ` ${String(tooltipModel.label)}`;
          },
          afterLabel: (tooltipModel) => {
            const score = tooltipModel.raw as {
              value: number;
              x: number;
              y: number;
            };
            if (sizeMetricLabel && typeof score.value !== undefined) {
              return ` ${sizeMetricLabel}: ${shorten(
                locale,
                score.value
              )} \n ${XMetricLabel}: ${shorten(
                locale,
                score.x
              )} \n ${YMetricLabel}: ${shorten(locale, score.y)}`;
            }
            return `${XMetricLabel}: ${shorten(
              locale,
              score.x
            )} \n ${YMetricLabel}: ${shorten(locale, score.y)}`;
          },
        },
        external: (context) => {
          if (isFullscreen) {
            return null;
          }
          const { chart, tooltip } = context;

          if (!tooltip.opacity) {
            closeCustomTooltip();
            return;
          }

          const dataPoints = tooltip.dataPoints;
          const serie = series[dataPoints[0].datasetIndex];
          const label = serie.label;
          const color = serie.color;
          const xValue = serie.data[0].x;
          const yValue = serie.data[0].y;
          const size = serie.data[0].size;

          openCustomTooltip(
            tooltip,
            chart,
            chartTooltip ? (
              chartTooltip({ label, color, size, xValue, yValue })
            ) : (
              <Tooltip
                locale={locale}
                XMetricLabel={XMetricLabel}
                YMetricLabel={YMetricLabel}
                label={label}
                color={color}
                xValue={xValue}
                yValue={yValue}
                size={size}
              />
            )
          );
        },
      },
      //@ts-ignore: datalabels is properly imported and registered on the chart
      datalabels: {
        offset: 2,
        padding: 0,
        font: {
          weight: "bold",
          size: 14,
        },
        display: "auto",
        anchor: function (context) {
          const value = context.dataset.data[
            context.dataIndex
          ] as BubbleDataPoint;
          return value.r < (context.dataset.label.length * 10) / 2
            ? "end"
            : "center";
        },
        align: function (context) {
          const value = context.dataset.data[
            context.dataIndex
          ] as BubbleDataPoint;
          return value.r < (context.dataset.label.length * 10) / 2
            ? "end"
            : "center";
        },
        color: function (context) {
          const value = context.dataset.data[
            context.dataIndex
          ] as BubbleDataPoint;
          return value.r < (context.dataset.label.length * 10) / 2
            ? String(context.dataset.backgroundColor)
            : Color.White;
        },
        formatter: function (_, context) {
          return context.dataset.label;
        },
      },
    },
  };
};
